import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';

import './earning.css';
import { CONFIG } from '../../../../shared/constants/common';
import {
  showWarning,
  showInfo,
  closeAlert,
  showCustomAlertWithReact
} from '../../../../shared/utils/swalHandler';
import { showManualSuccessPushToast } from '../../../../shared/pusNotification/useManualToasts';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import PendingBalanceInfoModal from '../../../Modals/EarningsPayout/PendingBalanceInfoModal';
import ReserveBalanceInfoModal from '../../../Modals/EarningsPayout/ReserveBalanceInfoModal';
import VatInfoModal from '../../../Modals/EarningsPayout/VatInfoModal';
import { requestPayout } from '../../../../shared/actions/transactionAction';
import { formatUsdAmountDisplay } from '../../../../shared/utils/function';
import { getVatRateByCountryCode } from '../../../../shared/constants/vatRates';
import { getGstRateByCountryCode } from '../../../../shared/constants/gstCountry';


const BalanceOverviewCard = ({
  user,
  balanceSummary,
  loading,
  isUsUser = false,
  w8FormFilled = false,
  w8FormExpired = false,
  w8PendingSigningUrl = null,
  w8StatusLoading = false,
  w9PendingSigningUrl = null,
  w9FormFilled = false,
  w9StatusLoading = false,
  totalEarnings = null,
  onPayoutSuccess
}) => {
  const navigate = useNavigation();
  const [pandingModal, setPandingModal] = useState(false);
  const [reserveModal, setReserveModal] = useState(false);
  const [valueAddedTaxModal, setValueAddedTaxModal] = useState(false);
  const [loadingPayout, setLoadingPayout] = useState(false);
  const [showAmount, setShowAmount] = useState(false);

  const getRequirements = (requirements) => {
    return Object.keys(requirements).map((key) => {
      const req = requirements[key];
      if (!req) return null;

      if (req.status === 201) return null;

      if (req.status === 200) {
        return (
          <li key={key} className="payout-requirement-item payout-requirement-success">
            <div className="payout-requirement-icon success">
              <i className="fa fa-check"></i>
            </div>
            <div className="payout-requirement-content">
              <span className="payout-requirement-text">{req.message}</span>
            </div>
          </li>
        );
      }

      // const params = {};
      // if (key === "w9form" && req.url) {
      //   params.w9FormLink = req.url;
      // }

      const isPending = req.status === 400;
      const isMissing = req.status === 404;

      return (
        <li
          key={key}
          className={`payout-requirement-item ${isPending ? 'payout-requirement-pending' : ''} ${isMissing ? 'payout-requirement-missing' : ''}`}
        >
          <div className={`payout-requirement-icon ${isPending ? 'pending' : 'missing'}`}>
            {isPending && <i className="fa fa-clock-o"></i>}
            {isMissing && <i className="fa fa-exclamation-circle"></i>}
          </div>
          <div className="payout-requirement-content">
            <span className="payout-requirement-text">
              {req.message}
              {isMissing && (
                <>
                  {' '}
                  <button
                    className="payout-requirement-link"
                    onClick={() => openModal(`modal_${key}`)}
                  >
                    Click here
                  </button>
                  {' '}to complete this requirement.
                </>
              )}
            </span>
          </div>
        </li>
      );
    });
  };

  // Restrict cash out for non-US users: W-8BEN must be filled and successfully submitted (not expired)
  const isW8CashOutRestricted = !isUsUser && (
    w8StatusLoading || !w8FormFilled || w8FormExpired || !!w8PendingSigningUrl
  );
  // US creators: restrict payout when $599+ earned until W9 is filled and submitted
  const effectiveTotalEarnings =
    totalEarnings != null
      ? Number(totalEarnings)
      :(Number(balanceSummary?.available) || 0);
      
  const isUsEarningsAtOrAboveW9Threshold = effectiveTotalEarnings >= CONFIG.MIN_BALANCE_TO_TAX_INFO;
  const isW9CashOutRestricted =
    isUsUser &&
    isUsEarningsAtOrAboveW9Threshold &&
    (w9StatusLoading || !!w9PendingSigningUrl || !w9FormFilled);
  const isCashOutRestricted = isW8CashOutRestricted || isW9CashOutRestricted;

  const handleRequestPayout = async () => {
    if (balanceSummary?.available < CONFIG.MIN_BALANCE_TO_CASH_OUT) {
      toast.error(`Insufficient balance. Minimum balance of $${CONFIG.MIN_BALANCE_TO_CASH_OUT} is required to cash out.`);
      return;
    }

    setLoadingPayout(true);
    try {
      const res = await requestPayout();
      if (res?.data?.status === 200) {
        showManualSuccessPushToast(
          'Payout Initiated',
          "Your payout has been successfully initiated. Once it's completed, the status in your payout history will update from Initiated to Processed.",
          user
        );
        setLoadingPayout(false);
        onPayoutSuccess?.();
      } else if (res?.data?.status === 401 || res?.data?.status === 400) {
        let requirements = null;
        if (res?.data && res.data?.requirements) {
          requirements = res.data?.requirements;
        }

        if (requirements === null) {
          await showInfo(
            "Info",
            res.data?.message,
            {
              showCloseButton: true
            }
          );
          setLoadingPayout(false);
        } else {
          await showCustomAlertWithReact(
            undefined,
            (
              <div className="payout-requirements-alert">
                <div className="payout-requirements-message">
                  {res.data?.message}
                </div>
                <div className="payout-requirements-divider"></div>
                <div className="payout-requirements-header">
                  <i className="fa fa-info-circle"></i>
                  <span>Please make sure you have a green checkmark on all the items below:</span>
                </div>
                <ul className="payout-requirements-list">
                  {getRequirements(requirements)}
                </ul>
                <div className="payout-requirements-footer">
                  <i className="fa fa-lightbulb-o"></i>
                  <span>
                    Once you have completed all the steps above, you will need to click "Request Payout" again.
                  </span>
                </div>
              </div>
            ),
            {
              showCloseButton: false,
              width: '600px',
              customClass: {
                popup: 'payout-requirements-popup',
                htmlContainer: 'payout-requirements-container'
              }
            }
          );
          setLoadingPayout(false);
        }
      } else {
        throw new Error(res?.message || res?.data?.message || res?.error || "Something went wrong. Please try again.");
      }
    } catch (res) {
      await showWarning(
        "Banking Details",
        res?.message || res?.error || "Something went wrong. Please try again.",
        {
          showCloseButton: true
        }
      );
      setLoadingPayout(false);
    }
  };

  const openModal = (modalName) => {
    closeAlert();
    if (modalName === "modal_masspay") {
      navigate.toBankAccount();
    }
  };

  const VAT_NOT_APPLICABLE = 'VAT NOT APPLICABLE';
  const GST_NOT_APPLICABLE = 'GST NOT APPLICABLE';
  const VAT_OR_GST_NOT_APPLICABLE = 'VAT NOT APPLICABLE or GST NOT APPLICABLE';

  const vatDisplayText = useMemo(() => {
    if (!user?.country) return null;

    const countryCode = user.country.toUpperCase();
    const vatCountry = getVatRateByCountryCode.find((r) => r.code === countryCode);
    // const gstCountry = getGstRateByCountryCode.find((r) => r.code === countryCode);
    const hasVatNumber = Boolean(user?.vat_number?.trim());
    const hasGstNumber = Boolean(user?.gst_number?.trim());

    if (vatCountry && hasVatNumber) return `VAT NUMBER ${user.vat_number}`;
    // if (gstCountry && hasGstNumber) return `GST NUMBER ${user.gst_number}`;

    // Hide for USA creators
    if (isUsUser) return null;
    // EU + UK: show VAT NOT APPLICABLE
    if (vatCountry) return VAT_NOT_APPLICABLE;
    // Canada, Australia, New Zealand: show GST NOT APPLICABLE
    // if (gstCountry) return GST_NOT_APPLICABLE;
    // Other countries: generic message
    // return VAT_OR_GST_NOT_APPLICABLE;
    return null;
  }, [user, isUsUser]);

  return (
    <div className={`g_card`}>
      <h3>
        <i className='fa-solid fa-wallet'></i> Balance Overview
      </h3>
      <p>A minimum balance of ${CONFIG.MIN_BALANCE_TO_CASH_OUT} is required to cash out.</p>
      <div className='cm_hr_full'></div>

      {loading ? (
        <div className="modern-referral-stats-loading">
          <div className="modern-referral-stats-spinner"></div>
          <p className="modern-referral-stats-loading-text">Loading...</p>
        </div>
      ) : (
        <>
          <div className='d-flex text-center pb-3 mb-3 border_bottom_gray text-uppercase'>
            <div className="w-full pp_label">
              <p className="d-flex align-items-center justify-center gap-2 mb-2">
                Pending
                <span
                  onClick={() => setPandingModal(true)}
                  className="question_btn_wrap"
                >
                  <i className='fa-solid fa-question'></i>
                </span>
              </p>
              <h5 style={{ color: '#ebb305' }} className="mb-0">
                ${formatUsdAmountDisplay(balanceSummary?.pending)}
              </h5>
            </div>
            <div className="w-full pp_label">
              <p className="d-flex align-items-center justify-center gap-2 mb-2">
                Available
                <span className="question_btn_wrap" onClick={() => setPandingModal(true)}>
                  <i className='fa-solid fa-question'></i>
                </span>
              </p>
              <h5 style={{ color: '#49de80' }} className="mb-0">
                ${formatUsdAmountDisplay(balanceSummary?.available)}
              </h5>
            </div>
          </div>

          <div className="text-center pp_label text-uppercase">
            <p className="d-flex align-items-center justify-center gap-2 mb-2">
              Reserve
              <span
                onClick={() => setReserveModal(true)}
                className="question_btn_wrap"
              >
                <i className='fa-solid fa-question'></i>
              </span>
              <span
                className='colleps_angle'
                onClick={() => setShowAmount(!showAmount)}
              >
                <FontAwesomeIcon icon={!showAmount ? faAngleUp : faAngleDown} />
              </span>
            </p>

            <h5 style={{ color: '#ebb305' }} className="mb-4">
              {showAmount ? `$${formatUsdAmountDisplay(balanceSummary?.reserve)}` : ''}
            </h5>

            <button
              className={`btn btn_primary`}
              onClick={handleRequestPayout}
              disabled={
                loadingPayout ||
                isCashOutRestricted ||
                (balanceSummary?.available || 0) < CONFIG.MIN_BALANCE_TO_CASH_OUT
              }
            >
              {loadingPayout ? 'Processing...' : 'Request Payout'}
            </button>

            {vatDisplayText && (
              <p className='mt-3 d-flex align-items-center justify-center gap-2'>
                {vatDisplayText}
                {(vatDisplayText.startsWith('VAT NUMBER') || vatDisplayText.startsWith('GST NUMBER')) && (
                  <span
                    className="question_btn_wrap"
                    onClick={() => setValueAddedTaxModal(true)}
                  >
                    <i className='fa-solid fa-question'></i>
                  </span>
                )}
              </p>
            )}
          </div>
        </>
      )}

      <PendingBalanceInfoModal
        isOpen={pandingModal}
        toggle={() => setPandingModal(false)}
      />

      <ReserveBalanceInfoModal
        isOpen={reserveModal}
        toggle={() => setReserveModal(false)}
      />

      <VatInfoModal
        isOpen={valueAddedTaxModal}
        toggle={() => setValueAddedTaxModal(false)}
      />
    </div>
  )
}

export default BalanceOverviewCard;